"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import Button from "../../../../components/Button";
import FilterSearch from "../../../../components/FilterSearch(name)";
import Table from "../../../../components/Table";
import Pagination from "../../../../components/Pagination";
import CustomerFamilyModal from "../../common/components/CustomerFamilyModal";
import styles from "../css/addCustomer.module.css";

export default function AddCustomer() {
  const navigate = useNavigate();
  const location = useLocation();
  const mode = useMemo(() => new URLSearchParams(location.search).get("mode"), [location.search]);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 4;
  const [familyOpen, setFamilyOpen] = useState(false);
  const [familyRows, setFamilyRows] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({
          search: search || "",
          page: String(currentPage),
          pageSize: String(pageSize),
        });
        const res = await fetch(`/frontshop/checkout/customers?${params.toString()}`);
        if (!res.ok) throw new Error(`Failed to load customers (${res.status})`);
        const json = await res.json();
        if (cancelled) return;
        setRows(Array.isArray(json?.data) ? json.data : []);
        setTotal(Number(json?.total || 0));
      } catch (e) {
        if (!cancelled) setError(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [search, currentPage]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  const pageData = useMemo(() => rows, [rows]);

  const columns = useMemo(
    () => [
      { header: "Surname", accessor: "surname" },
      { header: "Firstname", accessor: "firstname" },
      { header: "Initial", accessor: "initial" },
      { header: "Title", accessor: "title" },
      { header: "Address", accessor: "address" },
      {
        header: "Action",
        accessor: "action",
        render: (_value, row) => (
          <Button
            variant="primary"
            onClick={() => {
              openFamilyFor(row);
            }}
          >
            View
          </Button>
        ),
      },
    ],
    []
  );

  const familyColumns = useMemo(
    () => [
      { header: "Surname", accessor: "surname" },
      { header: "Initial", accessor: "initial" },
      { header: "Title", accessor: "title" },
      { header: "Role", accessor: "role" },
      {
        header: "Action",
        accessor: "action",
        render: (_v, row) => (
          <Button
            variant="primary"
            onClick={() => {
              console.log("Selected member/dependant", row);
              setFamilyOpen(false);
            }}
          >
            Select
          </Button>
        ),
      },
    ],
    []
  );

  async function openFamilyFor(main) {
    setSelectedCustomer(main);
    setFamilyRows([]);
    setFamilyOpen(true);
    try {
      const res = await fetch(`/frontshop/checkout/customer/${encodeURIComponent(main.id)}/details`);
      if (!res.ok) throw new Error("Failed to load family details");
      const json = await res.json();
      const list = [];
      if (json?.main) {
        list.push({
          patientid: json.main.patientid,
          surname: json.main.surname || "",
          initial: (json.main.firstname || "").slice(0, 1).toUpperCase(),
          title: json.main.title || "",
          role: "Main Member",
        });
      }
      const ben = Array.isArray(json?.beneficiaries) ? json.beneficiaries : [];
      for (const b of ben) {
        list.push({
          patientid: b.patientid,
          surname: b.surname || "",
          initial: (b.firstname || "").slice(0, 1).toUpperCase(),
          title: b.title || "",
          role: "Dependent",
          dependentcode: b.dependentcode || "",
        });
      }
      setFamilyRows(list);
    } catch {}
  }

  async function selectMember(member) {
    try {
      if (member?.patientid) {
        await fetch("/frontshop/checkout/customer/select", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ patientid: member.patientid }),
        });
      }
      const payload = {
        patientid: member.patientid,
        surname: member.surname,
        initial: member.initial,
        title: member.title,
        role: member.role,
      };
      try { localStorage.setItem("frontshop_customer", JSON.stringify(payload)); } catch {}
    } finally {
      setFamilyOpen(false);
      if (mode === "debtors") {
        navigate("/frontshop/debtors", { state: { selectedCustomer: true } });
      } else {
        navigate("/frontshop", { state: { selectedCustomer: true } });
      }
    }
  }

  return (
    <div className={styles.wrapper}>
      <Navbar />

      <div className={styles.container}>
        <div className={styles.toolbarRow}>
          <FilterSearch filterName="Filter" value={search} onChange={setSearch} placeholder="Search Name" />
        </div>

        <div className={styles.tableSection}>
          <Table columns={columns} data={pageData} />
        </div>

        <div className={styles.paginationContainer}>
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>

      {familyOpen && (
        <CustomerFamilyModal
          open={familyOpen}
          onClose={() => setFamilyOpen(false)}
          customer={selectedCustomer}
          rows={familyRows}
          onSelect={(member) => {
            selectMember(member);
          }}
          actionLabel={mode === "debtors" ? "Statement" : "Select"}
          title="Customer Details"
        />
      )}

      <div className={styles.footerWrapper}>
        <Footer />
      </div>
    </div>
  );
}

